import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
//
import { brands } from '../../data/brandsData';
//
import Layout from '../../components/Layout';
import BannerSliderView from './components/BannerSliderView';
import CategoryView from './components/CategoryView';
import RequestQuoteSection from './components/RequestQuoteSection';
import SliderView from '../../components/SliderView';
import FeaturedProductsView from './components/FeaturedProductsView';
import NewArrivalsView from './components/NewArrivalsView';
import EmailSubscribeView from './components/EmailSubscribeView';
import NewsletterView from './components/NewsletterView';
import BestSellersView from './components/BestSellersView';
import { getStoreInfo, getMenuList } from '../../api/services/systemService';
import { initUserInfo, setUserInfo } from '../../redux/userInfo';
import ShopByCategoryView from './components/ShopByCategoryView';
import { navigateToLogin } from '../../utils';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0 1.5%;
  justify-content: space-between;
`;

const BrandCard = styled.div`
  display: flex;
  width: 200px;
  height: 100px;
  align-items: center;
  justify-content: center;
  background-color: white;
  border-radius: 5px;
  padding: 0 30px;
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }

  img {
    max-width: 120px;
    max-height: 80px;
    width: auto;
    height: auto;
    object-fit: contain;
  }

  /* Mobile responsive */
  @media (max-width: 480px) {
    flex: 0 0 calc(50% - 4px);
    width: calc(50% - 4px);
    min-width: 0;
    max-width: none;
    height: 70px;
    padding: 0 6px;
    box-sizing: border-box;

    img {
      max-width: 70px;
      max-height: 50px;
    }
  }
`;

const BrandCardOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 5px;
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.3s ease-in-out,
    visibility 0.3s ease-in-out;

  ${BrandCard}:hover & {
    opacity: 1;
    visibility: visible;
  }
`;

const BrandSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding: 20px 30px 0 30px;

  @media (max-width: 768px) {
    padding: 15px 20px 0 20px;
  }

  @media (max-width: 480px) {
    padding: 10px 15px 0 15px;
  }
`;

const BrandSectionTitle = styled.h1`
  font-size: 30px;
  color: ${(props) => props.theme.colors.primaryText};
  margin: 0;
  font-weight: bold;

  @media (max-width: 768px) {
    font-size: 24px;
  }

  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const ViewAllButton = styled.button`
  background: none;
  border: none;
  color: #2b83bf;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.7;
  }

  @media (max-width: 768px) {
    font-size: 14px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

export default function HomePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state.userInfo);
  const scrollAttempted = useRef(false);
  // Shop By Brand / New Arrivals / Featured 카테고리가 이 webuser 에게 숨김 처리되면 메인 섹션도 숨김
  const [showBrandSection, setShowBrandSection] = useState(true);
  const [showNewArrivalsSection, setShowNewArrivalsSection] = useState(true);
  const [showFeaturedSection, setShowFeaturedSection] = useState(true);

  const token = localStorage.getItem('token');

  if (token === 'null' && userInfo.userId != 0) {
    // 토큰이 없는데 유저 정보가 있다 , 세션 초기화
    dispatch(initUserInfo());
  }

  useEffect(() => {
    getDbStoreInfo();
  }, []);

  useEffect(() => {
    // 메뉴 API 는 webuser 별 숨김 카테고리가 제외되어 내려옴 — 카테고리가 없으면 해당 섹션 숨김
    const checkSectionVisible = async () => {
      try {
        const result = await getMenuList(userInfo.userId);
        const titles = (result.menuList || []).map((cat) =>
          String(cat.cateNm).trim().toLowerCase(),
        );
        setShowBrandSection(titles.includes('shop by brand'));
        setShowNewArrivalsSection(titles.includes('new arrivals'));
        setShowFeaturedSection(titles.includes('featured'));
      } catch (e) {
        // 메뉴 조회 실패 시 기존 동작(표시) 유지
        setShowBrandSection(true);
        setShowNewArrivalsSection(true);
        setShowFeaturedSection(true);
      }
    };
    checkSectionVisible();
  }, [userInfo.userId]);

  useEffect(() => {
    // Check if we should scroll to category section after returning from category page
    const shouldScroll = sessionStorage.getItem('scrollToCategoryView');

    if (shouldScroll === 'true' && !scrollAttempted.current) {
      scrollAttempted.current = true;

      // Use multiple attempts to ensure scroll happens after all content loads
      const attemptScroll = (attempts = 0) => {
        if (attempts > 5) {
          // Clear flag after max attempts
          sessionStorage.removeItem('scrollToCategoryView');
          return;
        }

        const categorySection = document.getElementById('category-section');
        if (categorySection) {
          // Add small delay between attempts to catch layout changes
          setTimeout(
            () => {
              categorySection.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });

              // Clear the flag after successful scroll
              if (attempts === 3) {
                sessionStorage.removeItem('scrollToCategoryView');
              }
            },
            200 * (attempts + 1),
          );
        }
      };

      // Start scroll attempts
      attemptScroll(0);
      attemptScroll(1);
      attemptScroll(2);
      attemptScroll(3);
    }
  }, []);

  const getDbStoreInfo = async () => {
    const result = await getStoreInfo();
    dispatch(
      setUserInfo({
        idStore: result.storeInfo.idStore,
        zipcode: result.storeInfo.zipcode,
      }),
    );
  };

  return (
    <Layout>
      {/* 이미지 배너 슬라이더 */}
      <BannerSliderView />
      {/* Request Quote 섹션 */}
      <RequestQuoteSection />
      <Container>
        {/* 카테고리 카드 */}
        <CategoryView />
        {/* 슬라이더
        {hotProductList.length > 0 ? (
          <SliderView title={'Hot Items'}>
            {hotProductList.map((item, index) => (
              <div key={`product_hot_${index}`}>
                <ProductCard data={item} key={`product_${index}`} />
              </div>
            ))}
          </SliderView>
        ) : null}
        {newProductList.length > 0 ? (
          <SliderView title={'New Arrivals'}>
            {newProductList.map((item, index) => (
              <div key={`product_new_${index}`}>
                <ProductCard data={item} key={`product_${index}`} />
              </div>
            ))}
          </SliderView>
        ) : null}
        {restaurantProductList.length > 0 ? (
          <SliderView title={'Restaurant Supplies'}>
            {restaurantProductList.map((item, index) => (
              <div key={`product_restaurant_${index}`}>
                <ProductCard data={item} key={`product_${index}`} />
              </div>
            ))}
          </SliderView>
        ) : null} */}
      </Container>
      <BestSellersView />
      <EmailSubscribeView />
      {showNewArrivalsSection && <NewArrivalsView />}
      <NewsletterView />
      <ShopByCategoryView />
      {showFeaturedSection && <FeaturedProductsView />}
      {showBrandSection && (
        <Container>
          {/* 브랜드 카드 슬라이더 */}
          <div style={{ marginTop: '30px', backgroundColor: '#F6F6F6' }}>
            <BrandSectionHeader>
              <BrandSectionTitle>Shop By Brand</BrandSectionTitle>
              <ViewAllButton
                onClick={() => {
                  if (userInfo.userId != 0) {
                    navigate('/brands');
                  } else {
                    navigateToLogin(navigate, '/brands');
                  }
                }}
              >
                View All &gt;
              </ViewAllButton>
            </BrandSectionHeader>
            <SliderView style={{ backgroundColor: '#F6F6F6' }}>
              {brands.map((item, index) => (
                <BrandCard
                  onClick={() => {
                    // 브랜드명 -> 카테고리 URL 키 (Brands 페이지와 동일 규칙)
                    const categoryKey = item.name
                      .toLowerCase()
                      .replace(/[^a-z\s]/g, '')
                      .replace(/\s+/g, '-')
                      .replace(/-+/g, '-')
                      .replace(/^-|-$/g, '');
                    const targetLink = `/search?category=${encodeURIComponent(categoryKey)}&page=1`;
                    if (userInfo.userId != 0) {
                      navigate(targetLink);
                    } else {
                      navigateToLogin(navigate, targetLink);
                    }
                  }}
                  key={`brand_${index}`}
                >
                  <img src={item.logo} alt={item.name} />
                  <BrandCardOverlay />
                </BrandCard>
              ))}
            </SliderView>
          </div>
        </Container>
      )}
    </Layout>
  );
}
